import React, { FC, useEffect, useState } from "react"
import UpTriangleIcon from "../../icons/UpTriangleGrey.svg"
import DownTriangleIcon from "../../icons/DownTriangleGrey.svg"
import Ticon from "../../icons/T.svg"
import UpArrow from "../../icons/up_long_arrow_green.svg"
import GreenCheck from "../../icons/green_check.svg"
// import DownloadDropdown from "./DownloadDropdown"
import {
  addNavigationPath,
  getRawDataAsync,
  setDataset,
  setSelectedDataset,
} from "./DataQualitySlice"
import { useAppDispatch, useAppSelector } from "../../util/hooks"
import { IRawDataObject } from "../../util/interface"

const headers = [
  "Raw Data Name",
  "Average Number of Rows",
  "Table Columns",
  "Manual Quality Check Added",
  "AI Enabled Data Quality",
]

const TableHeader = ({ headers }: { headers: string[] }) => (
  <thead>
    <tr>
      {headers.map((header, index) => (
        <th
          key={index}
          className="px-4 pb-6 text-[11px] text-textGrey-50 text-left"
        >
          <div
            className={`flex gap-2 ${
              index === 0 ? "justify-start" : "justify-center"
            }`}
          >
            {header}
            <div className="flex flex-col">
              <img className="h-2" src={UpTriangleIcon} alt="" />
              <img className="h-2" src={DownTriangleIcon} alt="" />
            </div>
          </div>
        </th>
      ))}
    </tr>
  </thead>
)

const QualityCheckView = ({ label }: { label: string }) => (
  <div className="flex gap-2 px-1 items-center bg-bgGreen-50 h-4">
    <span className="w-3 h-3 rounded-full flex justify-center items-center bg-white">
      <img className="h-1/2" src={GreenCheck} alt="" loading="lazy" />
    </span>
    <p className="text-[9px] text-textGreen-100">{label}</p>
  </div>
)

interface IRawDataQualityRow {
  item: IRawDataObject
  index: number
}

const RawDataQualityTableRow: FC<IRawDataQualityRow> = ({ item, index }) => {
  const dispatch = useAppDispatch()

  const handleReviewClick = (dataSet: string) => {
    dispatch(setDataset(dataSet))
    dispatch(setSelectedDataset(dataSet))
    dispatch(
      addNavigationPath(
        `Dataset Selected (${
          dataSet.charAt(0).toUpperCase() + dataSet.slice(1).toLowerCase()
        })`,
      ),
    )
  }

  const formatString = (inputStr: string) =>
    inputStr
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")

  return (
    <tr className="text-base">
      <td className="border-t border-customGrey-300 px-4 py-5 font-bold text-textGrey-50">
        <div className="flex gap-2 items-center">
          <img className="h-4 w-4" src={Ticon} alt="" />
          {formatString(item.table_name)}
        </div>
      </td>
      <td className="px-4 py-5 text-center">
        <div className="flex grow justify-center items-center gap-2 w-full">
          <div className="flex w-2/4 justify-between items-center gap-2">
            <p className="text-textGrey-50">{item.average_count_of_rows}</p>
            <div className="flex gap-1 items-center justify-center w-[34px] bg-bgGreen-50 px-1">
              <img src={UpArrow} alt="" />
              <p className="text-xs text-textGreen-100">2%</p>
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-5 text-center">{item.total_number_of_column}</td>
      <td className="px-4 py-5 text-center">
        <div className="flex items-center justify-center">
          <div className="flex items-center gap-2 flex-wrap w-[260px]">
            {Object.entries(item.quality_check).map(
              ([key, value], index) =>
                value && (
                  <QualityCheckView key={index} label={formatString(key)} />
                ),
            )}
          </div>
        </div>
      </td>
      <td className="flex justify-center px-4 py-5 text-center">
        <div className="flex justify-center items-center gap-3">
          <button
            onClick={() => handleReviewClick(item.table_name)}
            className="flex w-3/4 h-9 justify-center items-center border border-dpTheme-main rounded-md cursor-pointer bg-white shadow-custom-2 transition-shadow duration-250 ease-in-out hover:shadow-custom-dark px-3"
          >
            <p className="text-xs text-dpTheme-main font-semibold">
              Review & Update
            </p>
          </button>
          <div className="flex flex-col text-[10px] leading-[1rem] h-9 justify-center">
            <p className="font-bold whitespace-nowrap text-left">
              4 Suggestions
            </p>
            <p className="whitespace-nowrap text-left">
              {index === 0
                ? 1
                : index === 1
                ? 2
                : index === 2
                ? 1
                : index === 3
                ? 0
                : 2}{" "}
              Enabled
            </p>
          </div>
        </div>
      </td>
    </tr>
  )
}

const RawDataTable = ({ data }: { data: IRawDataObject[] }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(5)

  const indexOfLastRow = currentPage * rowsPerPage
  const indexOfFirstRow = indexOfLastRow - rowsPerPage
  const currentRows = data.slice(indexOfFirstRow, indexOfLastRow)
  const totalPages = Math.ceil(data.length / rowsPerPage)

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setRowsPerPage(Number(event.target.value))
    setCurrentPage(1)
  }

  return (
    <div className="flex flex-col overflow-x-auto grow p-3">
      <table className="min-w-full bg-transparent font-semibold">
        <TableHeader headers={headers} />
        <tbody>
          {currentRows.map((item, index) => (
            <RawDataQualityTableRow key={index} index={index} item={item} />
          ))}
        </tbody>
      </table>
      <div className="flex justify-end items-center py-3 px-10 gap-10">
        <div className="flex items-center gap-2">
          <span className="text-[10px]">Rows per page:</span>
          <select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            className="border rounded w-11 outline-none text-xs"
          >
            {[5, 10, 15, 20].map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-7 text-[14px]">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="flex justify-center items-center disabled:opacity-50"
          >
            {"<"}
          </button>
          <p className="font-semibold">
            {currentPage}/{totalPages}
          </p>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="flex justify-center items-center disabled:opacity-50"
          >
            {">"}
          </button>
        </div>
      </div>
    </div>
  )
}

const RawData = () => {
  const dispatch = useAppDispatch()
  const { rawData: data, rawDataStatus } = useAppSelector(
    (state) => state.dataQuality.rawData,
  )
  const refresh = useAppSelector((state) => state.dataQuality.refreshHit)

  useEffect(() => {
    dispatch(getRawDataAsync())
  }, [dispatch, refresh])

  return (
    <div className="flex flex-col">
      <div className="flex flex-col my-4 grow bg-white rounded-lg">
        <div className="flex justify-end items-center p-3 gap-2">
          {/* <p className="text-[10px] text-[#333333] italic">
            *Click here to know about Raw Data Quality Report
          </p>
          <DownloadDropdown
            options={["Schema", "Rows Processed", "Unique & Not Null"]}
            onOptionClick={(option) => console.log(option)}
          /> */}
        </div>
        <div className="flex grow">
          {rawDataStatus === "idle" && <RawDataTable data={data} />}
        </div>
      </div>
    </div>
  )
}

export default RawData
